import { usePostHog } from "posthog-js/react";
import { useCallback, useRef } from "react";

import { NoData } from "@/components/NoData/NoData";
import { Stat } from "@/components/Stat/Stat";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { Container } from "@/design-system/Container";
import { Heading } from "@/design-system/Heading";
import { Spinner } from "@/design-system/Spinner";
import { Spread } from "@/design-system/Spread";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { useTranslation } from "@/translations";

/** One aggregate metric rendered in an operational analytics summary. */
export interface OperationalAnalyticsMetric {
  label: string;
  /** The aggregate value, or undefined when the API did not provide one. */
  value: number | undefined;
  /** Optional formatter for values that should not use the default numeric rendering. */
  renderValue?: (value: number | undefined) => React.ReactNode;
  /** Optional supporting comparison or context shown below the aggregate value. */
  renderFooter?: () => React.ReactNode;
}

type OperationalAnalyticsScope =
  | { scope: "project"; projectId: string; projectIds?: never }
  | { scope: "portfolio"; projectId?: never; projectIds: string[] };

type OperationalAnalyticsSummaryProps = OperationalAnalyticsScope & {
  module: "tickets" | "communityFeed" | "events" | "bookings";
  title: string;
  metrics?: OperationalAnalyticsMetric[];
  loadingMetrics: OperationalAnalyticsMetric[];
  detailsHref?: string;
  isLoading: boolean;
  error?: unknown;
  onReload: () => void;
};

/** Safe, aggregate-only analytics preview for operational overview pages. */
export function OperationalAnalyticsSummary({
  module,
  scope,
  projectId,
  projectIds,
  title,
  metrics,
  loadingMetrics,
  detailsHref,
  isLoading,
  error,
  onReload,
}: OperationalAnalyticsSummaryProps): React.ReactNode {
  const { t } = useTranslation();
  const postHog = usePostHog();
  const portfolioProjectIds = [...(projectIds ?? [])].sort();
  const portfolioProjectIdsKey = portfolioProjectIds.join(",");
  const impressionContextKey = `${module}:${scope}:${projectId ?? portfolioProjectIdsKey}`;
  const detailsAvailable = Boolean(detailsHref);
  const trackedImpressionContexts = useRef(new Set<string>());
  const captureAnalyticsEvent = useCallback(
    (event: "viewed_operational_analytics_summary" | "clicked_operational_analytics_more_details") => {
      postHog.capture(event, {
        module,
        scope,
        ...(scope === "project" ? { projectId } : {}),
        ...(scope === "portfolio"
          ? { projectIds: portfolioProjectIdsKey.split(",").filter(Boolean), projectCount: portfolioProjectIds.length }
          : {}),
        detailsAvailable,
      });
    },
    [detailsAvailable, module, portfolioProjectIds.length, portfolioProjectIdsKey, postHog, projectId, scope],
  );
  const handleImpression = useCallback(() => {
    if (trackedImpressionContexts.current.has(impressionContextKey)) {
      return;
    }

    trackedImpressionContexts.current.add(impressionContextKey);
    captureAnalyticsEvent("viewed_operational_analytics_summary");
  }, [captureAnalyticsEvent, impressionContextKey]);
  const intersectionRef = useOnIntersection({ onIntersect: handleImpression, threshold: 0.25 });
  const canTrackImpression = !isLoading && !error && metrics !== undefined;

  let content: React.ReactNode;
  if (error || (!isLoading && !metrics)) {
    content = <NoData error={error} onReload={onReload} horizontal />;
  } else {
    const visibleMetrics = isLoading ? loadingMetrics : metrics;

    content = (
      <div className="relative">
        <section aria-hidden={isLoading || undefined} className="flex flex-col gap-space-md @xs:flex-row @xs:flex-wrap">
          {visibleMetrics?.map((metric) => (
            <Stat
              key={metric.label}
              label={metric.label}
              value={metric.value}
              renderValue={metric.renderValue}
              renderFooter={metric.renderFooter}
              placeholder={isLoading}
            />
          ))}
        </section>
        {isLoading && (
          <div className="absolute inset-0 z-raised flex items-center justify-center bg-background-default/60">
            <Spinner size="lg" reveal="delayed" />
          </div>
        )}
      </div>
    );
  }

  return (
    <Container fullWidth>
      <Card
        as="article"
        ref={canTrackImpression ? intersectionRef : undefined}
        aria-busy={isLoading}
        data-testid={`operational-analytics-summary-${module}`}
      >
        <Spread space="md" align="center">
          <Heading as="h2" level={3}>
            {title}
          </Heading>
          {detailsHref && (
            <Button
              variant="ghostPrimary"
              type="link"
              href={detailsHref}
              onClick={() => captureAnalyticsEvent("clicked_operational_analytics_more_details")}
            >
              {t("common.action.see-more")}
            </Button>
          )}
        </Spread>
        {content}
      </Card>
    </Container>
  );
}
